import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { Field, FormikProvider, getIn, useFormik } from "formik";
import * as Yup from "yup";
import { preparationVehicle } from "../../../../common/services/vehicles.api";
import {
  IVehicle,
  VehicleRedoPreparationType,
} from "../../../../types/vehicle.type";

import "./ModalRedoPreparation.scss";
import { CheckboxField } from "../checkbox-field";
import TextAreaFormik from "../../../../common/components/form/TextAreaFormik";
import { Modal } from "flowbite-react";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { useEffect, useState } from "react";
import { UploadDocumentFieldFormik } from "@/modules/renters/components/forms/fields/UploadDocumentFieldFormik";
import { UploadImageField } from "@/common/components/form/UploadImageField/UploadImageField";
import { IEmployee } from "@/types/employee.type";
import { getPreparators } from "@/common/services/employee.api";

type PreparationVehicleProps = {
  formRequestErrors?: { message: string; path: string[] }[];
  vehicle: IVehicle;
  onClose: () => void;
};

export default function ModalRedoPreparationVehicle(
  props: PreparationVehicleProps
) {
  const { notifySuccess, notifyError } = useToastContext();
  const [loading, setLoading] = useState(false);
  const pathToUpload = `vehicles/${props.vehicle._id}/repreparation`;
  const [preparators, setPreparators] = useState<IEmployee[]>([]);
  const validationSchema = Yup.object().shape({
    cellphoneSupport: Yup.boolean(),
    engineProtection: Yup.boolean(),
    paintingProtection: Yup.boolean(),
    phoneChargerSupport: Yup.boolean(),
    hasSpareKey: Yup.boolean(),
    tracker: Yup.boolean(),
    repreparation: Yup.object(),
  });

  const emptyObject = {
    paintingProtection: false,
    tracker: false,
    cellphoneSupport: false,
    phoneChargerSupport: false,
    engineProtection: false,
    hasSpareKey: false,
  };

  const formik = useFormik({
    initialValues: {
      ...emptyObject,
      repreparator: "",
      repreparation: {},
    },
    validationSchema: validationSchema,
    onSubmit: async (data: VehicleRedoPreparationType) => {
      setLoading(true);
      const result = await preparationVehicle(
        props.vehicle.licensePlate,
        props.vehicle.model,
        data
      );
      if (result.ok) {
        notifySuccess("Repreparação realizada com sucesso");
      }

      if (!result.ok && result.message) {
        notifyError(result.message);
      }
      setLoading(false);
    },
  });

  useEffect(() => {
    async function loadPreparators() {
      const { ok, data } = await getPreparators();
      if (ok && data) {
        setPreparators(data);
      }
    }
    loadPreparators();
  }, []);
  0;
  const handleCheckboxChange = (name: keyof typeof emptyObject) => {
    const isChecked = getIn(formik.values, name);
    const newCheckedState = !isChecked;
    formik.setFieldValue(name, newCheckedState);

    if (name !== "hasSpareKey") {
      const currentRepreparation = formik.values.repreparation || {};
      if (newCheckedState) {
        formik.setFieldValue("repreparation", {
          ...currentRepreparation,
          [name]: { beforeImage: "", afterImage: "" },
        });
      } else {
        const newRepreparation = { ...currentRepreparation };
        delete newRepreparation[name];
        formik.setFieldValue("repreparation", newRepreparation);
      }
    }
  };

  return (
    <Modal show={true} size="xl" onClose={() => props.onClose()}>
      <Modal.Header className=" h-12 items-center dark:text-black">
        <span className="text-black dark:text-black">
          Repreparação: {props.vehicle.licensePlate}
        </span>
      </Modal.Header>

      <Modal.Body
        className="w-full relative border-gray flex flex-col text-black"
        style={{ minHeight: "auto" }}
      >
        <FormikProvider value={formik}>
          <div className="mb-4">
            <label
              htmlFor="repreparator"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Responsável pela Repreparação
            </label>
            <Field
              as="select"
              id="repreparator"
              name="repreparator"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="">Selecione um responsável...</option>
              {preparators.map((employee) => (
                <option key={employee._id} value={employee.name}>
                  {employee.name}
                </option>
              ))}
            </Field>
          </div>
          <hr className="mb-4" />
          {/* Item 1: Proteção de pintura */}
          <CheckboxField
            name="paintingProtection"
            label="Proteção de pintura (PP):"
            onChange={() => handleCheckboxChange("paintingProtection")}
          />
          {formik.values.paintingProtection && (
            <div className="mt-2 mb-2 pl-6 grid grid-cols-2 gap-4 border-l-2">
              <UploadDocumentFieldFormik
                name="repreparation.paintingProtection.beforeImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
              <UploadDocumentFieldFormik
                name="repreparation.paintingProtection.afterImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
            </div>
          )}

          {/* Item 2: Rastreador */}
          <CheckboxField
            name="tracker"
            className="bg-gray/25"
            label="Rastreador (RT):"
            onChange={() => handleCheckboxChange("tracker")}
          />
          {formik.values.tracker && (
            <div className="mt-2 mb-2 pl-6 grid grid-cols-2 gap-4 border-l-2">
              <UploadDocumentFieldFormik
                name="repreparation.tracker.beforeImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
              <UploadDocumentFieldFormik
                name="repreparation.tracker.afterImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
            </div>
          )}

          {/* Repita a estrutura para os outros checkboxes... */}
          <CheckboxField
            name="cellphoneSupport"
            label="Suporte de telefone (SC):"
            onChange={() => handleCheckboxChange("cellphoneSupport")}
          />
          {formik.values.cellphoneSupport && (
            <div className="mt-2 mb-2 pl-6 grid grid-cols-2 gap-4 border-l-2">
              <UploadDocumentFieldFormik
                name="repreparation.cellphoneSupport.beforeImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
              <UploadDocumentFieldFormik
                name="repreparation.cellphoneSupport.afterImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
            </div>
          )}

          <CheckboxField
            name="phoneChargerSupport"
            className="bg-gray/25"
            label="Carregador de celular (CC):"
            onChange={() => handleCheckboxChange("phoneChargerSupport")}
          />
          {formik.values.phoneChargerSupport && (
            <div className="mt-2 mb-2 pl-6 grid grid-cols-2 gap-4 border-l-2">
              <UploadDocumentFieldFormik
                name="repreparation.phoneChargerSupport.beforeImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
              <UploadDocumentFieldFormik
                name="repreparation.phoneChargerSupport.afterImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
            </div>
          )}

          <CheckboxField
            name="engineProtection"
            label="Proteção da carenagem (PC):"
            onChange={() => handleCheckboxChange("engineProtection")}
          />
          {formik.values.engineProtection && (
            <div className="mt-2 mb-2 pl-6 grid grid-cols-2 gap-4 border-l-2">
              <UploadDocumentFieldFormik
                name="repreparation.engineProtection.beforeImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
              <UploadDocumentFieldFormik
                name="repreparation.engineProtection.afterImage"
                pathToUpload={pathToUpload}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentFieldFormik>
            </div>
          )}
          <CheckboxField
            name="hasSpareKey"
            label="Possui chave Reserva (CR):"
            onChange={() => handleCheckboxChange("hasSpareKey")}
          />

          <TextAreaFormik name="observation" label="Observação" />
        </FormikProvider>
      </Modal.Body>

      <Modal.Footer className="justify-end h-14 ">
        <ButtonWithLoader isLoading={loading} onClick={formik.handleSubmit}>
          Enviar
        </ButtonWithLoader>
      </Modal.Footer>
    </Modal>
  );
}
